/**
 * The two dictionary reads the quota filters render from (task T.R4).
 *
 * WHY THIS LIVES IN THE PAGE MODULE: T.R4 owns `pages/quotas/**` only, so both
 * clients sit beside their only consumer instead of `apps/web/src/api/`; whoever
 * merges moves them next to `quotas.ts`/`venues.ts`. The shapes mirror the
 * generated api types 1:1 — the same rule `../../api/quotas` documents for the
 * shared barrel that cannot re-export `openapi/api-types.ts`.
 *
 *   * #21 `GET /api/programs` — the course dictionary the program pills group
 *     from (see `buildCoursePills`).
 *   * #6  `GET /api/venues`   — the venue directory the «Площадка» combobox
 *     searches. #12 carries venue NAMES only, so an address search has nowhere
 *     else to come from; the read is `fetchVenueDirectory`'s page walk (T.Z136) —
 *     the api's own `MAX_PAGE_SIZE` is 200 and the campaign has passed it.
 *
 * WHY THE VENUE READ IS NOT `useVenues()`: `venuesQueryKey` folds the query into
 * `{region, q, page}` and drops `pageSize`, so a 200-row directory fetch would
 * share a cache entry with the roster's 50-row page 1 — and swap the roster's
 * rows out from under it. A private key keeps the two reads apart.
 */

import { useQuery } from '@tanstack/react-query';

import { requestEnvelope } from '../../api/client';
import type { CourseFormat } from '../../api/quotas';

// ---------------------------------------------------------------------------
// #21 — the course dictionary
// ---------------------------------------------------------------------------

/** Mirrors `ProgramDto` (packages/shared/src/openapi/api-types.ts). */
export interface ProgramDto {
  courseId: number;
  name: string;
  /** The audience the variant addresses — `8-9 кл.` / `10-11 кл.` / `СПО` / `college`. */
  audience: string | null;
  /** Variants the course actually runs, from `course_info`. */
  formats: CourseFormat[];
  active: boolean;
  /**
   * ADDITIVE (v1.8.0, A10) — «ИИ 8-9» / «Python СПО», the caption the ЦА headers show;
   * set (or cleared) by ADMIN through #21.1. `null`/absent ⇒ the UI keeps showing `courseId`.
   */
  shortName?: string | null;
}

export type ProgramListEnvelope = { data: ProgramDto[] };

export function programsPath(): string {
  return '/programs';
}

export async function fetchPrograms(signal?: AbortSignal): Promise<ProgramListEnvelope> {
  return requestEnvelope<ProgramDto[]>('GET', programsPath(), ['data'], { signal });
}

export const PROGRAMS_QUERY_KEY = ['programs', 'dictionary'] as const;

/**
 * #21 — the dictionary. `retry: 0` like every read here: the pills fall back to
 * the grid's own slice when the endpoint is not there yet, and a retrying 404
 * would only delay that fallback.
 */
export function usePrograms() {
  return useQuery({
    queryKey: PROGRAMS_QUERY_KEY,
    queryFn: ({ signal }) => fetchPrograms(signal),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

// ---------------------------------------------------------------------------
// #6 — the venue directory (one page, enough for the 189 venues of the campaign)
// ---------------------------------------------------------------------------

/**
 * Wave 9 (T.Y9): the directory lives in ONE place now — `api/venues.ts`, whose
 * entry carries the kind/partner facts the settings blocks read alongside the
 * `id`/`name`/`address` the comboboxes always needed. Two fetchers of the same
 * endpoint (and the same cache key) with two entry shapes was a divergence
 * waiting to happen; this module keeps its import path for the pages that
 * already use it.
 */
export {
  fetchVenueDirectory,
  useVenueDirectory,
  venueDirectoryPath,
  VENUE_DIRECTORY_PAGE_SIZE as DIRECTORY_PAGE_SIZE,
  VENUE_DIRECTORY_QUERY_KEY,
} from '../../api/venues';
export type { VenueDirectoryEntry } from '../../api/venues';
